import React, { FC, useEffect, useState } from 'react';
import axios from '../../../utils/axios';
import { API_URL } from '../../../constants';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import { PageWrapper } from '../../../styledComponents/wrappers';
import {
  InventoryAdjustmentReason,
  InventoryAdjustmentReasonConfig,
} from '../../../services/inventory/types';
import AdjustmentReasonModal from './AdjustmentReasonModal';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { Box, Chip, Tooltip, makeStyles } from '@material-ui/core';
import LabelValuePairDisplay from '../../tradeups/detailComponents/LabelValuePairDisplay';

interface Permission {
  id: number;
  name: string;
}

const useTooltipStyles = makeStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: theme.shadows[12],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    maxWidth: 500,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  arrow: {
    color: theme.palette.common.white,
    '&::before': {
      border: `1px solid ${theme.palette.grey[300]}`,
    },
  },
}));

const getDirectionLabel = (direction?: string): string => {
  switch (direction) {
    case 'increase':
      return 'Increase only';
    case 'decrease':
      return 'Decrease only';
    default:
      return 'Both';
  }
};

const ConfigCellDisplay: FC<{
  config?: InventoryAdjustmentReasonConfig;
  reason?: InventoryAdjustmentReason;
  permissions?: Permission[];
}> = ({ config, reason, permissions = [] }) => {
  const classes = useTooltipStyles();
  const exportToNetsuite = config?.exportToNetsuite ?? reason?.exportToNetsuite ?? false;
  const exportToShopify = config?.exportToShopify ?? reason?.exportToShopify ?? false;
  const amountEligible = config?.amountEligible ?? reason?.amountEligible ?? false;
  const allowInventoryCreate = config?.allowInventoryCreate ?? false;
  const quantityAdjustmentDirection = config?.quantityAdjustmentDirection ?? 'both';
  const netsuiteAccount = config?.netsuiteAccount;

  const chips: React.ReactNode[] = [];

  if (exportToNetsuite) {
    const accountLabel =
      netsuiteAccount?.accountName || netsuiteAccount?.accountId?.toString() || '';
    chips.push(
      <Chip
        key="netsuite"
        label={`NetSuite${accountLabel ? ` (${accountLabel})` : ''}`}
        size="small"
        color="primary"
        variant="outlined"
        style={{ marginRight: 4, marginBottom: 4 }}
      />,
    );
  }
  if (exportToShopify) {
    chips.push(
      <Chip
        key="shopify"
        label="Shopify"
        size="small"
        color="secondary"
        variant="outlined"
        style={{ marginRight: 4, marginBottom: 4 }}
      />,
    );
  }
  if (amountEligible) {
    chips.push(
      <Chip
        key="amount"
        label="Amount Eligible"
        size="small"
        style={{ marginRight: 4, marginBottom: 4 }}
      />,
    );
  }
  if (allowInventoryCreate) {
    chips.push(
      <Chip
        key="create"
        label="Allow Create"
        size="small"
        color="default"
        style={{ marginRight: 4, marginBottom: 4, backgroundColor: '#e3f2fd' }}
      />,
    );
  }
  if (quantityAdjustmentDirection !== 'both') {
    chips.push(
      <Chip
        key="direction"
        label={quantityAdjustmentDirection === 'increase' ? 'Qty Increase' : 'Qty Decrease'}
        size="small"
        color="default"
        style={{
          marginRight: 4,
          marginBottom: 4,
          backgroundColor: quantityAdjustmentDirection === 'increase' ? '#c8e6c9' : '#ffcdd2',
        }}
      />,
    );
  }

  if (chips.length === 0) {
    return <span style={{ color: '#999' }}>None</span>;
  }

  const permissionNames =
    reason?.permissionList && permissions.length > 0
      ? reason.permissionList
          .map(id => {
            const permission = permissions.find(p => p.id === id);
            return permission ? permission.name.split('_').join(' ') : null;
          })
          .filter(Boolean)
          .join(', ') || 'None'
      : reason?.permissionList?.length
      ? `${reason.permissionList.length} permission(s)`
      : 'None';

  const labelValuePairs = [
    {
      label: 'Export to NetSuite',
      value: exportToNetsuite ? 'Yes' : 'No',
    },
    {
      label: 'NetSuite Account',
      value: netsuiteAccount
        ? netsuiteAccount.accountName || `ID: ${netsuiteAccount.accountId}`
        : 'None',
    },
    {
      label: 'Export to Shopify',
      value: exportToShopify ? 'Yes' : 'No',
    },
    {
      label: 'Amount Eligible',
      value: amountEligible ? 'Yes' : 'No',
    },
    {
      label: 'Allow Inventory Create',
      value: allowInventoryCreate ? 'Yes' : 'No',
    },
    {
      label: 'Qty Adjustment Direction',
      value: getDirectionLabel(quantityAdjustmentDirection),
    },
    {
      label: 'Permissions',
      value: permissionNames,
    },
  ];

  return (
    <Tooltip
      classes={{
        tooltip: classes.tooltip,
        arrow: classes.arrow,
      }}
      title={
        <Box>
          <LabelValuePairDisplay
            labelValuePairs={labelValuePairs}
            gridSize={{ label: 4, value: 8 }}
          />
        </Box>
      }
      arrow
      enterDelay={300}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {chips}
      </Box>
    </Tooltip>
  );
};

const AdjustmentReasonsAdmin: FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [reasons, setReasons] = useState<InventoryAdjustmentReason[]>([]);
  const [editingReason, setEditingReason] = useState<InventoryAdjustmentReason | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [modalError, setModalError] = useState<boolean>(false);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 15, page: 0 });
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'code', headerName: 'Code', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    {
      field: 'active',
      headerName: 'Active',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (params.value ? 'Yes' : 'No'),
    },
    {
      field: 'configSummary',
      headerName: 'Config',
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <ConfigCellDisplay
          config={params.row.config}
          reason={params.row}
          permissions={permissions}
        />
      ),
    },
  ];

  const fetchReasons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/inventory/inventoryAdjustmentReasons?includeInactive`,
      );
      setReasons((response as AxiosResponse<InventoryAdjustmentReason[]>).data || []);
    } catch (err) {
      console.error('Error fetching adjustment reasons:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await axios.get(`${API_URL}/admin/users/permissions`);
        setPermissions((response as AxiosResponse<Permission[]>).data || []);
      } catch (err) {
        console.error('Error fetching permissions:', err);
      }
    };
    fetchReasons();
    fetchPermissions();
  }, []);

  const handleRowClick = async (params: GridRowParams) => {
    setModalError(false);
    setEditingReason(params.row);
  };

  const extractPermissions = (formState: Record<string, any>): number[] => {
    return Array.isArray(formState.permissions) ? formState.permissions : [];
  };

  const handleCreateSuccess = async (formState: Record<string, any>) => {
    setModalLoading(true);
    setModalError(false);
    try {
      const netsuiteAccount = formState.netsuiteAccountId
        ? {
            accountId: parseInt(formState.netsuiteAccountId, 10),
            accountName: formState.netsuiteAccountName || undefined,
          }
        : null;

      const config: InventoryAdjustmentReasonConfig = {
        exportToNetsuite: formState.exportToNetsuite ?? false,
        exportToShopify: formState.exportToShopify ?? false,
        netsuiteAccount,
        amountEligible: formState.amountEligible ?? false,
        allowInventoryCreate: formState.allowInventoryCreate ?? false,
        quantityAdjustmentDirection: formState.quantityAdjustmentDirection ?? 'both',
      };

      const permissionList = extractPermissions(formState);

      await axios.post(`${API_URL}/inventory/inventoryAdjustmentReasons`, {
        code: formState.code,
        description: formState.description,
        active: formState.active ?? true,
        permissionList,
        config,
      });
      setCreateModalOpen(false);
      await fetchReasons();
    } catch (err) {
      console.error('Error creating adjustment reason:', err);
      setModalError(true);
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditSuccess = async (formState: Record<string, any>) => {
    if (!editingReason) return;
    setModalLoading(true);
    setModalError(false);
    try {
      const netsuiteAccount = formState.netsuiteAccountId
        ? {
            accountId: parseInt(formState.netsuiteAccountId, 10),
            accountName: formState.netsuiteAccountName || undefined,
          }
        : null;

      const config: InventoryAdjustmentReasonConfig = {
        exportToNetsuite: formState.exportToNetsuite ?? false,
        exportToShopify: formState.exportToShopify ?? false,
        netsuiteAccount,
        amountEligible: formState.amountEligible ?? false,
        allowInventoryCreate: formState.allowInventoryCreate ?? false,
        quantityAdjustmentDirection: formState.quantityAdjustmentDirection ?? 'both',
      };

      const permissionList = extractPermissions(formState);

      await axios.put(`${API_URL}/inventory/inventoryAdjustmentReasons/${editingReason.id}`, {
        code: formState.code,
        description: formState.description,
        active: formState.active ?? true,
        permissionList,
        config,
      });
      setEditingReason(null);
      await fetchReasons();
    } catch (err) {
      console.error('Error updating adjustment reason:', err);
      setModalError(true);
    } finally {
      setModalLoading(false);
    }
  };

  const toggleEditModal = () => {
    setEditingReason(null);
    setModalError(false);
  };

  const toggleCreateModal = () => {
    setCreateModalOpen(!createModalOpen);
    setModalError(false);
  };

  return (
    <PageWrapper style={{ overflow: 'hidden' }}>
      <span className="list-title-nav-wrapper">
        <button className="classy-button" onClick={toggleCreateModal}>
          Add New Adjustment Reason
        </button>
      </span>
      {editingReason && (
        <AdjustmentReasonModal
          formSuccess={handleEditSuccess}
          selectedReason={editingReason}
          toggleModal={toggleEditModal}
          error={modalError}
          loading={modalLoading}
        />
      )}
      {createModalOpen && (
        <AdjustmentReasonModal
          formSuccess={handleCreateSuccess}
          toggleModal={toggleCreateModal}
          error={modalError}
          loading={modalLoading}
        />
      )}
      <p className="count-field">Total Adjustment Reasons: {reasons.length}</p>
      <StripedDataGrid
        columns={columns}
        rows={reasons}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[15, 30, 60]}
        onRowClick={handleRowClick}
      />
    </PageWrapper>
  );
};

export default AdjustmentReasonsAdmin;
